
import { Component, inject, input, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, map, mergeMap, of, switchMap } from 'rxjs';
import { Address } from '../../core/models/core/address.model';
import { AddressFormComponent } from '../address-form/address-form.component';
import { AccountAddressesService } from '../../core/services/account/account-addresses.service';
import { GenericObject } from '../../core/models/core/generic-object';
import { ErrorsService } from '../../core/services/errors.service';
import { AddressService } from '../../core/services/address.service';
import { AddressValidationResult } from '../../core/models/core/address-validation-result.model';
import { OrderService } from '../../core/services/order.service';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { TranslocoPipe } from '@ngneat/transloco';
import { AuditService } from '../../core/services/audit.service';
import { OrderType } from '../../core/models/order-type.enum';
import { OrderTypeConfig } from '../../core/models/order-type-config.model';

@Component({
  selector: 'cwo-edit-address',
  standalone: true,
  imports: [AddressFormComponent, CommonModule, AsyncButtonDirective, TranslocoPipe],
  templateUrl: './edit-address.component.html',
  styleUrl: './edit-address.component.scss'
})
export class EditAddressComponent{
  accountAddressesService = inject(AccountAddressesService);
  addressService = inject(AddressService);
  errorsService = inject(ErrorsService);
  orderService = inject(OrderService);
  private auditService = inject(AuditService);

  address = input<Address | undefined>();
  hideDone = input(false);
  isInCheckout = input(false);
  isSavedAddress = input(false);
  selectedAddress = input<Address>();
  orderType = input<OrderTypeConfig | undefined>();

  allErrors: GenericObject<string[]> | undefined;
  validationResponse: AddressValidationResult | undefined = undefined;
  isAddressFormValid: boolean = false;

  loadingSubscription: Subscription | null = null;
  readonly addressFormComponent = viewChild<AddressFormComponent>('addressFormComp');
  readonly closeEdit = output<Address | null>();
  readonly deliveryInstructionExpanded = output<boolean>();
  readonly orderAddress = output<Address>();

  private invalidAddressFormInstructions() {
    const addressFormComponent = this.addressFormComponent();
    if(!addressFormComponent) return;

    if(!addressFormComponent.deliveryInstructions.valid) {
      addressFormComponent.handleExpansion(true);
    }
  }

  done(overrideAddress = false) {
    
    const addressFormComponent = this.addressFormComponent();
    if(!addressFormComponent?.addressForm.valid){
      this.invalidAddressFormInstructions();
      addressFormComponent?.enableErrors();
      return;
    }

    if(this.validationResponse && !overrideAddress){
      addressFormComponent?.toggleHighlightOverrideMessage();
      return;
    }
    const address = new Address().deserialize(addressFormComponent?.addressForm.value);
    this.auditService.createAudit(() => `Address has been updated: {addressName: ${address.name}, fullAddress: ${address.street}, ${address.city}, ${address.state} ${address.zip}, ${address.diff1 && `${address.diff1}: ${address.value1}`}${address.diff2 ? `, ${address.diff2}: ${address.value2}` : ''}}`);
    if(overrideAddress){
      address.force = true;
      if(this.isSavedAddress()){
        this.loadingSubscription = this.accountAddressesService.updateAddress(address).pipe(
          switchMap(() => this.accountAddressesService.fetchAddresses()),
          switchMap(() => {
            if(this.address()?.name === this.orderService.order()?.customer?.address?.name){
              return this.orderService.updateOrderCustomerAddress(address);
            } else {
              return of(null);
            }
          })
        ).subscribe({
          next: (address) => {
            this.loadingSubscription = null;
            this.closeEdit.emit(address);
          },
          error: (error) => {
            this.loadingSubscription = null;
            const parsedError = this.errorsService.parseErrorResponse(error);
            this.allErrors = parsedError.errors;   
          }
        });
      } else {
        this.loadingSubscription = this.orderService.updateOrderCustomerAddress(address).pipe(mergeMap(() => {
          if (
            this.addressFormComponent()?.addressForm?.controls[
              'saveToAddressBook'
            ].value
          ) {
            return this.accountAddressesService.addAddress(address).pipe(mergeMap(() => this.accountAddressesService.fetchAddresses().pipe(map(() => address))));
          } else {
            return of(address);
          }
        })).subscribe({
          next: (address) => {
            this.loadingSubscription = null;
            this.closeEdit.emit(address);          
          },
          error: (error) => {
            this.loadingSubscription = null;
            const parsedError = this.errorsService.parseErrorResponse(error);
            this.allErrors = parsedError.errors;   
          }
        })
      } 
      
    } else {
      this.loadingSubscription = this.validateAddressAndProceed(address).pipe(
        switchMap((validationResponse) => {
          // Check if validationResponse contains errors
          if (validationResponse?.error) {
            return of({validationResponse, address}); // Return validationResponse to handle it later
          }
    
          if (this.addressFormComponent()?.addressForm?.controls['saveToAddressBook'].value) {
            return this.accountAddressesService.addAddress(address).pipe(
              map(() => ({validationResponse, address})) // Continue passing validation response
            );
          } else {
            return of({validationResponse, address}); // Pass the validation response along
          }
        }),
      ).subscribe({
        next: ({validationResponse, address}) => {
          this.loadingSubscription = null;
          if (validationResponse?.error) {
            this.addressFormComponent()?.enableErrors();
            this.validationResponse = validationResponse;
          } else {
            this.allErrors = undefined;
            this.validationResponse = undefined;
            this.closeEdit.emit(address);
            if(this.addressFormComponent()?.addressForm?.controls['saveToAddressBook'].value) this.auditService.createAudit(() => `New address has been created: {addressName: ${this.address()?.name}, fullAddress: ${this.address()?.street}, ${this.address()?.city}, ${this.address()?.state?.toUpperCase()} ${this.address()?.zip}, ${this.address()?.diff1 && `${this.address()?.diff1}: ${this.address()?.value1}`}${this.address()?.diff2 ? `, ${this.address()?.diff2}: ${this.address()?.value2}` : ''}}`);
          }
        },
        error: (error) => {
          this.loadingSubscription = null;
          const parsedError = this.errorsService.parseErrorResponse(error);
          this.allErrors = parsedError.errors;
        },
      });
    }
  }

validateAddressAndProceed = (address: Address) => {
    const addressValidation$ = this.isSavedAddress() ? this.accountAddressesService.validateExistingAddress(address) : this.orderService.validateAddress(address, this.orderService.order()?.deferTime ?? undefined);
    return addressValidation$.pipe(
      switchMap((validationResponse) => {
        if (validationResponse?.error) {
          // Return observable that completes without further processing
          return of(validationResponse);
        }
        if(this.isSavedAddress()){
          return this.accountAddressesService.updateAddress(address).pipe(
            switchMap(() => {
              return this.accountAddressesService.fetchAddresses().pipe(
                map(() => validationResponse)
              )
            }),
            switchMap(() => {
              if ( this.address()?.name === (this.selectedAddress()?.name ?? '') &&
  this.orderService.order()?.customer) {
                const orderCopy = this.orderService.order().copy();
                orderCopy.orderType = OrderType.Delivery;

                return this.orderService.updateOrder(orderCopy).pipe(
                  switchMap(() => {
                    return this.orderService
                      .updateOrderCustomerAddress(address)
                      .pipe(map(() => validationResponse));
                  })
                );
              } else {
                if(this.address()?.name === this.orderService.order()?.customer?.address?.name){
                  return this.orderService.updateOrderCustomerAddress(address).pipe(
                    map(() => validationResponse)
                  )
                } else {
                  return of(validationResponse);
                }
              }
            })
          );
        } else {
          return of(validationResponse);
        }
      })
    );
  };

  handleOverrideAddress() {
    this.done(true);
  }

  handleFormChanging() {
    this.validationResponse = undefined;
    this.allErrors = undefined;
  }

  handleNoThankyou() {
    this.auditService.createAudit(() => "Cancel button clicked on Change Store window.");
    this.validationResponse = undefined;
  }

  handleDeliveryInstructionExpanded(value: boolean) {
    this.deliveryInstructionExpanded.emit(value);
  }
}
